import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { blocked } from 'flare-chat-core/contracts/chat/operation-result';
import { matchesOperationDescriptor, operationSubmissionResult } from 'flare-chat-core/contracts/workflow/operations';
import { createSubmissionState } from './submission-state';
import {
  attachWorkflowOperationActions,
  buildWorkflowOperationRequest,
  workflowOperationActionKey,
} from '../../projection/workflow/operations.js';

const descriptorsFrom = (items) => items.flatMap((item) => item?.workflowOperationActions ?? []);
const currentDescriptor = (state, action) => state.descriptors.find((descriptor) => (
  descriptor.available && matchesOperationDescriptor(action, descriptor)
));
const receiptKey = (sessionId, descriptor) => JSON.stringify([
  sessionId, descriptor.action_key, descriptor.operation_type,
  Object.entries(descriptor.target).sort(([left], [right]) => left.localeCompare(right)),
]);

/** @param {import('./contracts').SubmissionOptions} options */
export function useWorkflowOperationController({
  renderedTimelineItems = [], runAPI = null, onRefreshSession, activeSessionId = '',
} = {}) {
  const adapterAvailable = typeof runAPI?.admitOperation === 'function';
  const [pendingActionKey, setPendingActionKey] = useState('');
  const submissions = useRef(createSubmissionState());
  const scope = useMemo(() => ({ active: true, activeSessionId, runAPI }), [activeSessionId, runAPI]);
  const timelineItems = useMemo(() => attachWorkflowOperationActions(renderedTimelineItems, {
    pendingActionKey, adapterAvailable,
  }), [adapterAvailable, pendingActionKey, renderedTimelineItems]);
  const currentRef = useRef({ scope, descriptors: [] });
  currentRef.current = { scope, descriptors: descriptorsFrom(timelineItems) };
  useEffect(() => {
    scope.active = true;
    setPendingActionKey('');
    return () => { scope.active = false; };
  }, [scope]);

  const handleWorkflowOperationAction = useCallback(async (action) => {
    const descriptor = currentDescriptor(currentRef.current, action);
    if (!scope.active || !descriptor || !adapterAvailable) return blocked('operation_unavailable');
    const isCurrent = () => scope.active && currentRef.current.scope === scope;
    setPendingActionKey(workflowOperationActionKey(descriptor));
    const result = await submissions.current.execute({
      key: receiptKey(activeSessionId, descriptor),
      request: buildWorkflowOperationRequest(descriptor),
      operation: descriptor.operation_type, isCurrent,
      submit: async (request) => operationSubmissionResult(
        await runAPI.admitOperation(descriptor.target.run_id, request), request,
      ),
      refresh: async () => { await onRefreshSession?.(); },
    });
    if (isCurrent() && result.reason !== 'submission_pending') setPendingActionKey('');
    return result;
  }, [activeSessionId, adapterAvailable, onRefreshSession, runAPI, scope]);

  return { handleWorkflowOperationAction, renderedTimelineItems: timelineItems };
}
export default useWorkflowOperationController;
